<template>
  <div class="mb-3 card card-sm card-choice">
    <div class="card-header">
      {{ title }}
      <template v-if="choice">
        <button
          class="ms-auto btn btn-sm btn-success"
          @click.prevent="emit('change-choice')"
        >
          Change
        </button>
        <button
          class="ms-2 btn btn-sm btn-danger"
          @click.prevent="emit('remove-choice')"
        >
          Remove
        </button>
      </template>
    </div>
    <div
      v-if="choice"
      class="card-body"
    >
      <div class="row">
        <dl
          v-if="!withSubSpecialisations"
          class="col"
        >
          <dt>Country</dt>
          <dd>{{ choice.countryName }}</dd>
        </dl>
        <dl class="col">
          <dt>Public</dt>
          <dd>{{ choice.functions }}</dd>
        </dl>
        <dl class="col">
          <dt>Available places</dt>
          <dd>{{ choice.availablePlaces }}</dd>
        </dl>
        <dl
          v-if="hasSpecialisation"
          class="col"
        >
          <dt>Specialisation</dt>
          <dd>{{ choice.specialisation }}</dd>
        </dl>
      </div>
      <hr>
      <div class="row">
        <div
          v-if="!withSubSpecialisations && choice.exchangeTypeItems.length"
          class="mb-2 col-4"
        >
          <label
            :for="`choice${idx}Type`"
            class="form-label required"
          >
            Type
          </label>
          <select
            :id="`choice${idx}Type`"
            v-model="choice.exchangeTypeId"
            class="form-select form-select-sm"
          >
            <option
              v-for="exType of choice.exchangeTypeItems"
              :key="exType.id"
              :value="exType.id"
            >
              {{ exType.name }}
            </option>
          </select>
        </div>
        <div
          v-if="choice.languageItems.length"
          class="mb-2 col-4"
        >
          <label
            :for="`choice${idx}Language`"
            class="form-label"
            :class="{ required: !choice.anyLanguageOnly }"
          >
            Language
          </label>
          <input
            v-if="choice.anyLanguageOnly"
            :id="`choice${idx}Language`"
            :value="choice.languageItems[0].name"
            type="text"
            class="form-control form-control-sm"
            readonly
          >
          <select
            v-else
            :id="`choice${idx}Language`"
            v-model="choice.languageId"
            class="form-select form-select-sm"
          >
            <option
              v-for="language of choice.languageItems"
              :key="language.id"
              :value="language.id"
            >
              {{ language.name }}
            </option>
          </select>
        </div>
        <div
          v-if="choice.durationItems.length"
          class="mb-2 col-4"
        >
          <label
            :for="`choice${idx}Duration`"
            class="form-label required"
          >
            Length
          </label>
          <select
            :id="`choice${idx}Duration`"
            v-model="choice.durationId"
            class="form-select form-select-sm"
          >
            <option
              v-for="duration of choice.durationItems"
              :key="duration.id"
              :value="duration.id"
            >
              {{ duration.name }}
            </option>
          </select>
        </div>
      </div>
      <div>
        <label class="form-label mb-2">Preferred period</label>
        <input
          v-model="choice.preferredPeriod"
          type="text"
          class="form-control form-control-sm"
        >
        <small class="form-text text-muted">Until the end of the applicable Programme year</small>
      </div>
      <div
        v-if="choice.allowPreMatchedParticipantToBeNamed"
        class="row gx-2 mt-3"
      >
        <div class="col-12 mb-0">
          <label
            :for="`choice${idx}PreMatchCounterpartName`"
            class="form-label"
          >
            If you have agreed a pre-match with a law enforcement counterpart, please enter their name below
          </label>
          <input
            :id="`choice${idx}PreMatchCounterpartName`"
            v-model="choice.preMatchCounterpartName"
            type="text"
            class="form-control form-control-sm"
          >
        </div>
      </div>
      <div
        v-if="choice.askAboutHostingMultipleCounterparts"
        class="row gx-2 mt-3"
      >
        <div class="col-12 mb-0">
          <div class="form-check">
            <input
              :id="`choice${idx}HasMultipleCounterparts`"
              v-model="choice.hasMultipleCounterparts"
              type="checkbox"
              class="form-check-input"
            >
            <label
              class="form-check-label"
              :for="`choice${idx}HasMultipleCounterparts`"
            >
              I am willing to host more than one law enforcement counterpart
            </label>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="card-body m-4 text-center"
    >
      <button
        type="button"
        class="btn btn-info"
        @click.prevent="emit('change-choice')"
      >
        Add first choice
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { OspeExchangeChoice } from '@/js/ospe-types'

defineProps({
  title: { type: String, default: '' },
  idx: { type: Number, default: 0 },
})

const choice = defineModel<OspeExchangeChoice | null>('choice', { required: true })

const emit = defineEmits<{
  'change-choice': []
  'remove-choice': []
}>()

const withSubSpecialisations = computed((): boolean => {
  return choice.value?.withSubSpecialisations === true
})

const hasSpecialisation = computed((): boolean => {
  return typeof choice.value?.specialisation === 'string' && choice.value.specialisation.trim().length > 0
})

</script>
